import React, { ReactNode, useEffect, useRef, useState, useCallback } from "react";
import Input from "../Input/Input";
import classNames from "classnames";
import { t } from "i18next";

interface props {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  title?: string | ReactNode;
  placeholder?: string;
  error?: string;
  className?: string;
  titleClass?: string;
  required?: boolean;
  runValidation?: boolean;
  customValidation?: ((value: unknown) => string | undefined) | Array<(value: unknown) => string | undefined>;
  autoFocus?: boolean;
  onValidate?: (error: string | undefined) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const Postalcode = ({
  value,
  onChange,
  onValidate,
  title,
  placeholder,
  error,
  className,
  titleClass,
  required,
  runValidation,
  customValidation,
  autoFocus,
  onFocus,
  onBlur: onBlurProp,
}: props) => {
  const [internalError, setInternalError] = useState(error || "");
  const errorRef = useRef<string>(error || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current && inputRef.current.focus();
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (errorRef.current !== internalError) {
      errorRef.current = internalError;
      onValidate && onValidate(internalError);
    }
  }, [internalError, onValidate]);

  useEffect(() => {
    if (errorRef.current !== error && error !== undefined) {
      setInternalError(error);
    }
  }, [error]);

  const validate = useCallback(() => {
    if (required && !value) {
      setInternalError(t("validations.required") || "");
    }
  }, [required, value]);

  useEffect(() => {
    if (runValidation) {
      validate();
    }
  }, [runValidation, validate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!!internalError) {
      setInternalError("");
    }
    onChange(e);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    validate();
    onBlurProp && onBlurProp(e);
  };

  return (
    <Input
      className={classNames(className)}
      titleClass={titleClass}
      name="postalcode"
      title={title}
      placeholder={placeholder || t("sof.postalCode.placeholder") || ""}
      aria-label={t("titles.postalCode")}
      value={value}
      onChange={handleChange}
      maxLength={5}
      errorMessage={internalError}
      error={!!internalError}
      showValidation={!!internalError}
      numeric
      onBlur={onBlur}
      onFocus={onFocus}
      validate={customValidation}
      reference={inputRef}
      onValidate={onValidate}
    />
  );
};

export default Postalcode;
